import { z } from "zod";
import { imageEmbeddingSchema } from "./embedding.js";
import { propertiesSchema } from "./properties.js";
import { searchTextSchema } from "./searchable-text.js";
export const BARCODE_TYPES = ["isbn", "ean", "upc", "unknown"];
/** Closed UI suggestion set; item category storage stays open (ADR-0003). */
export const ITEM_CATEGORY_SUGGESTIONS = ["book", "food", "device", "media", "other"];
/** Trim + lowercase an item category; the only normalization writes may use. */
export function normalizeItemCategory(value) {
    return value.trim().toLowerCase();
}
/** Write-side category: normalized and non-empty. Reads keep the stored value. */
export const itemCategorySchema = z
    .string()
    .transform(normalizeItemCategory)
    .refine((value) => value.length > 0, { message: "category must not be empty" });
const OTHER_CATEGORY = "other";
const KNOWN_CATEGORIES = ITEM_CATEGORY_SUGGESTIONS.filter((category) => category !== OTHER_CATEGORY);
/** Custom values sort after every known suggestion and before `other`. */
const CUSTOM_CATEGORY_RANK = KNOWN_CATEGORIES.length;
const OTHER_CATEGORY_RANK = CUSTOM_CATEGORY_RANK + 1;
const CATEGORY_SORT_RANK = new Map([
    ...KNOWN_CATEGORIES.map((category, index) => [category, index]),
    [OTHER_CATEGORY, OTHER_CATEGORY_RANK],
]);
/**
 * Settled category order: `book`, `food`, `device`, `media`, custom values
 * alphabetically, then `other` last. Shared by the server's `sort=category`
 * path and the frontend's plain per-container order.
 */
export function compareItemCategories(left, right) {
    const leftRank = CATEGORY_SORT_RANK.get(left) ?? CUSTOM_CATEGORY_RANK;
    const rightRank = CATEGORY_SORT_RANK.get(right) ?? CUSTOM_CATEGORY_RANK;
    if (leftRank !== rightRank)
        return leftRank - rightRank;
    if (leftRank === CUSTOM_CATEGORY_RANK)
        return left.localeCompare(right);
    return 0;
}
export const ITEM_PHOTO_TYPES = ["general", "nutrition"];
/** A single photo belonging to an item, stored locally with content-addressed MD5 ID. */
export const itemPhotoSchema = z.object({
    id: z.string().regex(/^[a-f0-9]{32}$/),
    url: z.string().min(1),
    type: z.enum(ITEM_PHOTO_TYPES),
    metadata: z.record(z.unknown()).optional(),
    createdAt: z.string().datetime(),
    /** On-disk extension (e.g. "jpg", "heic"), resolved once at store time so the
     *  temp `.uploading` name never leaks into the stored filename (crash fix). */
    ext: z.string().min(1).optional(),
    /** Mime type resolved at store time (HEIC/JPEG/etc.), used for Content-Type. */
    mime: z.string().min(1).optional(),
    /** Downscaled preview filename generated by the CDC preview pipeline (QR-like). */
    preview: z.string().min(1).optional(),
    /** Image embedding produced by the CDC image-embedding pipeline (ticket 04). */
    embedding: imageEmbeddingSchema.optional(),
});
export const itemSchema = z.object({
    id: z.string().uuid(),
    barcode: z.string().min(1),
    barcodeType: z.enum(BARCODE_TYPES),
    name: z.string().min(1),
    category: z.string().refine((value) => value.trim().length > 0, {
        message: "category must not be empty",
    }),
    description: z.string().default(""),
    photo: z.string().url().nullable().default(null),
    photos: z.array(itemPhotoSchema).default([]),
    metadata: z.record(z.unknown()).default({}),
    /** Typed custom properties (ADR-0037); optional on legacy records and
     *  coerced to `{}` when the item store loads items.json. Declared here so
     *  the frontend's Zod parse does not silently strip the field. */
    properties: propertiesSchema.optional(),
    createdAt: z.string().datetime(),
    data: z
        .object({
        searchText: searchTextSchema.optional(),
    })
        .optional(),
});
export const containerItemSchema = z.object({
    containerId: z.string().uuid(),
    itemId: z.string().uuid(),
    quantity: z.number().int().nonnegative(),
    addedAt: z.string().datetime(),
});
/** One placement plus its stored item: the item routes' list/add/update shape. */
export const containerItemResponseSchema = containerItemSchema.extend({
    item: itemSchema,
});
/** `GET /api/containers/:id/items` — every placement in a container. */
export const containerItemsResponseSchema = z.array(containerItemResponseSchema);
/**
 * `GET /api/items/lookup/:barcode` — the external provider preview for a
 * barcode no stored item matches (404 when the provider knows nothing).
 */
export const barcodeLookupResponseSchema = z.object({
    name: z.string().min(1),
    category: z.enum(["book", "food", "other"]),
    photo: z.string().url().nullable().default(null),
    metadata: z.record(z.unknown()).default({}),
});
/** `POST /api/items/refresh` — per-id failures travel alongside the count. */
export const itemRefreshResponseSchema = z.object({
    refreshed: z.number().int().nonnegative(),
    errors: z.array(z.string()),
});
/** `POST /api/items/bulk-delete` — how many items were permanently removed. */
export const bulkDeleteItemsResponseSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
// ── Zod schemas for API validation ──────────────────────────────────
export const addItemSchema = z.object({
    barcode: z.string().min(1),
    quantity: z.number().int().positive().default(1),
});
export const updateQuantitySchema = z.object({
    quantity: z.number().int().nonnegative(),
});
/** Edit an item's editable details (ticket 02): name, category, the metadata
 *  rows and the typed date properties. `metadata` and `properties` are
 *  replaced wholesale — row/date add/remove is the client's job.
 *  `description` (ticket 09) is a first-class markdown field. */
export const updateItemSchema = z.object({
    name: z.string().min(1).optional(),
    category: itemCategorySchema.optional(),
    description: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    properties: propertiesSchema.optional(),
});
/** Item-list filter options (filter-table-reuse 02): value + contextual count. */
export const itemFacetOptionSchema = z.object({
    value: z.string(),
    count: z.number().int().nonnegative(),
});
export const itemFacetsSchema = z.object({
    owners: z.array(itemFacetOptionSchema),
    tags: z.array(itemFacetOptionSchema),
    categories: z.array(itemFacetOptionSchema),
});
export const EMPTY_ITEM_FACETS = { owners: [], tags: [], categories: [] };
export const barcodeLookupParamsSchema = z.object({
    barcode: z.string().min(1),
});
export const containerItemParamsSchema = z.object({
    id: z.string().uuid(),
    itemId: z.string().uuid(),
});
export const containerItemsParamsSchema = z.object({
    id: z.string().uuid(),
});
export const itemPhotoParamsSchema = z.object({
    id: z.string().uuid(),
    photoId: z.string().regex(/^[a-f0-9]{32}$/),
});
/** Reorder an item's photos — `ids` must be an exact permutation of the
 *  item's current photo ids (first photo is the cover / list icon). */
export const reorderItemPhotosSchema = z.object({
    ids: z.array(z.string().regex(/^[a-f0-9]{32}$/)).min(1),
});
//# sourceMappingURL=item.js.map